/**
 * Align panel — drives the align tool (place one splat onto another by
 * clicking the same 3 features in each). Shows what to click next, how many
 * picks have landed, and offers undo for both a stray pick and the applied
 * transform.
 *
 * All state lives in the tool; this panel only reflects `align.*` events.
 */

import { Container, Label, Button } from '@playcanvas/pcui';

import type { Events } from '../../events';
import type { Scene } from '../../scene';
import { i18n } from '../../ui/localization';

interface AlignState {
    phase: 'source' | 'target';
    src: number;
    dst: number;
    pairs: number;
    minPairs: number;
    sourceName: string | null;
    targetName: string | null;
    canSwitch: boolean;
    canApply: boolean;
    canUndo: boolean;
    fitScale: number | null;
    worstError: number | null;
    worstIndex: number;
    degenerate: boolean;
}

class AlignPanel extends Container {
    constructor(events: Events, _scene: Scene, args = {}) {
        args = {
            ...args,
            id: 'align-panel',
            class: 'panel',
            hidden: true
        };
        super(args);

        // Block viewport gestures from passing through the panel.
        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((evt) => {
            this.dom.addEventListener(evt, (e: Event) => e.stopPropagation());
        });

        let toolActive = false;
        let state: AlignState = {
            phase: 'source', src: 0, dst: 0, pairs: 0, minPairs: 3,
            sourceName: null, targetName: null,
            canSwitch: false, canApply: false, canUndo: false,
            fitScale: null, worstError: null, worstIndex: -1, degenerate: false
        };

        // ── Header ──────────────────────────────────────────────────────
        const header = new Container({ class: 'panel-header' });
        header.append(new Label({ class: 'panel-header-icon', text: '' }));
        header.append(new Label({ class: 'panel-header-label', text: i18n.t('ollo.align.title') }));
        this.append(header);

        // ── Primary action ──────────────────────────────────────────────
        const primary = new Container({ class: 'align-buttons' });
        const startButton = new Button({ class: ['align-button-primary'], text: i18n.t('ollo.align.start') });
        startButton.on('click', () => events.fire('tool.align'));
        primary.append(startButton);
        this.append(primary);

        // ── Guidance ────────────────────────────────────────────────────
        const statusLabel = new Label({ class: 'align-hint', text: '' });
        this.append(statusLabel);

        // ── Readouts ────────────────────────────────────────────────────
        const movingRow = new Container({ class: 'align-row' });
        movingRow.append(new Label({ class: 'align-row-label', text: i18n.t('ollo.align.moving') }));
        const movingValue = new Label({ class: 'align-row-value', text: '—' });
        movingRow.append(movingValue);
        this.append(movingRow);

        const srcRow = new Container({ class: 'align-row' });
        srcRow.append(new Label({ class: 'align-row-label', text: i18n.t('ollo.align.picks-on-it') }));
        const srcValue = new Label({ class: 'align-row-value', text: '0' });
        srcRow.append(srcValue);
        this.append(srcRow);

        const dstRow = new Container({ class: 'align-row' });
        dstRow.append(new Label({ class: 'align-row-label', text: i18n.t('ollo.align.picks-on-target') }));
        const dstValue = new Label({ class: 'align-row-value', text: '0' });
        dstRow.append(dstValue);
        this.append(dstRow);

        // Fit preview — the numbers that say whether the picks agree BEFORE
        // anything moves.
        const scaleRow = new Container({ class: 'align-row' });
        scaleRow.append(new Label({ class: 'align-row-label', text: i18n.t('ollo.align.fit-scale') }));
        const scaleValue = new Label({ class: 'align-row-value', text: '—' });
        scaleRow.append(scaleValue);
        this.append(scaleRow);

        const errRow = new Container({ class: 'align-row' });
        errRow.append(new Label({ class: 'align-row-label', text: i18n.t('ollo.align.worst-pair') }));
        const errValue = new Label({ class: 'align-row-value', text: '—' });
        errRow.append(errValue);
        this.append(errRow);

        // ── Buttons ─────────────────────────────────────────────────────
        const switchButtons = new Container({ class: 'align-buttons' });
        const switchButton = new Button({ text: i18n.t('ollo.align.pick-target') });
        switchButton.on('click', () => events.fire('align.switchToTarget'));
        switchButtons.append(switchButton);
        this.append(switchButtons);

        const applyButtons = new Container({ class: 'align-buttons' });
        const applyButton = new Button({ class: ['align-button-primary'], text: i18n.t('ollo.align.apply') });
        applyButton.on('click', () => events.fire('align.apply'));
        applyButtons.append(applyButton);
        this.append(applyButtons);

        const buttons = new Container({ class: 'align-buttons' });
        const undoPickButton = new Button({ text: i18n.t('ollo.align.undo-pick') });
        undoPickButton.on('click', () => events.fire('align.pick.undo'));
        buttons.append(undoPickButton);

        const restartButton = new Button({ text: i18n.t('ollo.align.restart') });
        restartButton.on('click', () => events.fire('align.reset'));
        buttons.append(restartButton);
        this.append(buttons);

        const undoRow = new Container({ class: 'align-buttons' });
        const undoAlignButton = new Button({ text: i18n.t('ollo.align.undo-align') });
        undoAlignButton.on('click', () => events.fire('align.undo'));
        undoRow.append(undoAlignButton);
        this.append(undoRow);

        const helpLabel = new Label({
            class: 'align-hint',
            text: i18n.t('ollo.align.hint')
        });
        this.append(helpLabel);

        // ── Live sync ───────────────────────────────────────────────────
        const sync = () => {
            movingValue.text = state.sourceName ?? '—';
            srcValue.text = String(state.src);
            dstValue.text = state.src > 0 ? `${state.dst} of ${state.src}` : String(state.dst);
            scaleValue.text = state.fitScale !== null ? `×${state.fitScale.toFixed(4)}` : '—';
            errValue.text = state.worstError !== null
                ? `${(state.worstError * 100).toFixed(1)} cm${state.worstIndex >= 0 ? ` (pick ${state.worstIndex + 1})` : ''}`
                : '—';
            switchButton.enabled = toolActive && state.canSwitch;
            switchButton.hidden = state.phase === 'target';
            applyButton.enabled = toolActive && state.canApply;
            undoPickButton.enabled = toolActive && (state.src > 0 || state.dst > 0);
            restartButton.enabled = toolActive && (state.src > 0 || state.dst > 0);
            undoAlignButton.enabled = state.canUndo;
        };

        events.on('align.changed', (s: AlignState) => { state = s; sync(); });
        events.on('align.status', (msg: string) => { statusLabel.text = msg; });

        events.on('tool.activated', (name: string) => {
            if (name === 'align') {
                toolActive = true;
                startButton.text = i18n.t('ollo.align.picking');
                startButton.class.add('align-button-active');
                sync();
            }
        });
        events.on('tool.deactivated', (name: string) => {
            if (name === 'align') {
                toolActive = false;
                startButton.text = i18n.t('ollo.align.start');
                startButton.class.remove('align-button-active');
                sync();
            }
        });

        sync();
    }
}

export { AlignPanel };
